import React, { createContext, useContext, useState, useEffect } from "react";
import { notificationService } from "../services/notificationService";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notifications
  const fetchNotifications = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationService.getNotifications(params);

      // Ensure data is an array
      const notificationArray = Array.isArray(data) ? data : [];
      setNotifications(notificationArray);

      // Update unread count
      const unread = notificationArray.filter((n) => !n.is_read).length;
      setUnreadCount(unread);

      console.log(
        `Loaded ${notificationArray.length} notifications, ${unread} unread`
      );
    } catch (err) {
      setError(err.message);
      console.error("Failed to fetch notifications:", err);

      // Set empty array on error to prevent UI issues
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count only
  const fetchUnreadCount = async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
      console.log(`Updated unread count: ${count}`);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );

      // Update unread count
      setUnreadCount((prev) => Math.max(0, prev - 1));

      console.log(`Marked notification ${notificationId} as read`);
    } catch (err) {
      setError(err.message);
      console.error("Failed to mark notification as read:", err);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);

      // Update local state
      const notification = notifications.find((n) => n.id === notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

      // Update unread count if it was unread
      if (notification && !notification.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      console.log(`Deleted notification ${notificationId}`);
    } catch (err) {
      setError(err.message);
      console.error("Failed to delete notification:", err);
    }
  };

  // Add new notification (for real-time updates)
  const addNotification = (notification) => {
    setNotifications((prev) => [notification, ...prev]);
    if (!notification.is_read) {
      setUnreadCount((prev) => prev + 1);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter((n) => !n.is_read);

    try {
      // Mark all unread notifications as read
      await Promise.all(
        unreadNotifications.map((n) => notificationService.markAsRead(n.id))
      );

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);

      console.log(`Marked ${unreadNotifications.length} notifications as read`);
    } catch (err) {
      setError(err.message);
      console.error("Failed to mark all notifications as read:", err);
    }
  };

  // CREATE TEST NOTIFICATION METHOD
  const createTestNotification = async (type = "order_update") => {
    try {
      const result = await notificationService.createTestNotification(type);

      // Refresh notifications to show the new one
      await fetchNotifications();

      return result;
    } catch (err) {
      console.error("Failed to create test notification:", err);
      setError("Failed to create test notification");
    }
  };

  // Initial load and polling
  useEffect(() => {
    fetchNotifications();

    // Set up polling for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    deleteNotification,
    addNotification,
    markAllAsRead,
    createTestNotification, // Add this for testing
    refreshNotifications: fetchNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
